import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { FileText, ExternalLink, Eye, Download, AlertCircle, Loader2 } from 'lucide-react';
import coverageApiService from '../../../services/coverage-api.service';
import { useToast } from '../../../components/ui/toast';
import { sendToKinesis } from '../../../analytics/kinesisTransport';
import { useSelector } from 'react-redux';

/**
 * Parse HTML string containing anchor tags to extract links
 */
const parseDocumentLinks = (htmlString) => {
  if (!htmlString) return [];

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    const links = doc.querySelectorAll('a');

    return Array.from(links).map((link) => ({
      text: link.textContent?.trim() || '',
      url: link.href,
    }));
  } catch (error) {
    console.error('Error parsing document links:', error);
    return [];
  }
};

/**
 * FormularyDocumentsContent - Displays formulary documents for a specific indication
 */
const FormularyDocumentsContent = ({ indicationId, productId, formularyId, cachedData, onCacheUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [loadingDocId, setLoadingDocId] = useState(null);
  const { showToast} = useToast();
  
  // Get user and search context for analytics
  const userAttributes = useSelector((state) => state.auth?.userAttributes || {});
  const searchParams = useSelector((state) => state.coverage?.searchParams || {});

  const documentLinks = cachedData?.documentLinks || [];
  const documents = cachedData?.documents || [];
  const error = cachedData?.error || null;

  const fetchData = useCallback(async () => {
    if (cachedData) {
      console.log('[Cache] Cache hit - using cached formulary documents for indication:', indicationId);
      return;
    }

    console.log('[Cache] Cache miss - fetching formulary documents for indication:', indicationId);
    setLoading(true);

    try {
      // Fetch document links and documents list in parallel
      const [linksResponse, docsResponse] = await Promise.all([
        coverageApiService.getFormularyDocumentLinks(productId, formularyId, indicationId),
        coverageApiService.getFormularyDocumentsList(productId, formularyId, indicationId),
      ]);

      const result = {
        documentLinks: [],
        documents: [],
        error: null,
      };

      // Process document links
      if (linksResponse.result?.DocumentLinks?.[0]?.URLs) {
        result.documentLinks = parseDocumentLinks(linksResponse.result.DocumentLinks[0].URLs);
      }

      // Process documents list
      if (docsResponse.result && Array.isArray(docsResponse.result)) {
        result.documents = docsResponse.result;
      }

      onCacheUpdate(result);
      
      // Track MMIT API calls for formulary documents (2 parallel calls)
      try {
        sendToKinesis('coverage_events', 'MMIT_API_CALLS_MADE', {
          userid: userAttributes?.sub || 'anonymous',
          organizationid: userAttributes?.['custom:org_owner_sub'] || userAttributes?.sub || 'anonymous',
          practice_id: userAttributes?.['custom:organization_id'] || '',
          session_id: window.currentSessionId || '',
          feature_module: 'formulary_documents',
          mmit_api_call_count: 2,
          indicationId: indicationId?.toString() || '',
          planId: productId?.toString() || '',
          drugId: formularyId?.toString() || '',
          timestamp: new Date().toISOString()
        });
      } catch (analyticsError) {
        console.error('Analytics error (non-blocking):', analyticsError);
      }
    } catch (err) {
      console.error('Error fetching formulary documents:', err);
      onCacheUpdate({
        documentLinks: [],
        documents: [],
        error: err.message || 'Failed to load documents',
      });
    } finally {
      setLoading(false);
    }
  }, [cachedData, indicationId, productId, formularyId, onCacheUpdate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleViewDocument = async (documentId, documentName, documentType) => {
    setLoadingDocId(documentId);
    showToast('Opening in new tab...', 'info', 2000);
    
    try {
      const response = await coverageApiService.getFormularyDocumentById(documentId);

      if (response.result?.presigned_url) {
        // Open directly without pop-up blocker check
        window.open(response.result.presigned_url, '_blank', 'noopener,noreferrer');
      } else {
        showToast('Unable to retrieve document URL', 'error', 4000);
        console.error('Unable to retrieve document URL');
      }
    } catch (err) {
      showToast('Failed to load document', 'error', 4000);
      console.error('Failed to load document:', err);
    } finally {
      setLoadingDocId(null);
    }
  };

  const handleDownloadDocument = async (documentId, documentName, documentType) => {
    setLoadingDocId(documentId);
    showToast('Downloading...', 'info', 2000);
    
    // Track document download event
    try {
      await sendToKinesis({
        eventtype: 'COVERAGE_DOCUMENT_DOWNLOADED',
        userid: userAttributes.sub || userAttributes['cognito:username'] || 'anonymous',
        useremail: userAttributes.email || 'unknown',
        planId: searchParams.selectedPlan?.planid || '',
        planName: searchParams.selectedPlan?.planname || '',
        payer: searchParams.selectedPlan?.payer || '',
        pbm: searchParams.selectedPlan?.pbm || '',
        drugId: searchParams.selectedDrug?.drugid || '',
        drugName: searchParams.selectedDrug?.drugname || '',
        documentType: documentType || '',
        documentName: documentName || '',
        documentId: documentId || '',
        indicationId: indicationId || '',
        timestamp: new Date().toISOString()
      });
    } catch (analyticsError) {
      console.error('Analytics error (non-blocking):', analyticsError);
    }
    
    try {
      const response = await coverageApiService.getFormularyDocumentById(documentId);

      if (response.result?.presigned_url) {
        // Use fetch with mode: 'no-cors' if CORS is an issue, otherwise just use the presigned URL directly
        try {
          // Try to fetch as blob first
          const pdfResponse = await fetch(response.result.presigned_url);
          const blob = await pdfResponse.blob();
          
          // Create a download link
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${documentName}.pdf`;
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          // Clean up the blob URL
          setTimeout(() => window.URL.revokeObjectURL(url), 100);
          showToast('Download started successfully', 'success', 3000);
        } catch (fetchError) {
          // If fetch fails due to CORS, fallback to direct link with target='_blank'
          console.warn('Fetch failed, using direct download link:', fetchError);
          const link = document.createElement('a');
          link.href = response.result.presigned_url;
          link.download = `${documentName}.pdf`;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          showToast('Download opened in new tab', 'success', 3000);
        }
      } else {
        showToast('Unable to retrieve document URL', 'error', 4000);
        console.error('Unable to retrieve document URL');
      }
    } catch (err) {
      showToast('Failed to download document', 'error', 4000);
      console.error('Failed to download document:', err);
    } finally {
      setLoadingDocId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4 bg-muted/30 rounded-lg border-2 border-dashed">
        <Loader2 className="h-10 w-10 text-primary animate-spin mb-4" />
        <p className="text-sm font-medium text-foreground mb-1">
          Loading Formulary Documents...
        </p>
        <p className="text-xs font-normal text-muted-foreground text-center max-w-md">
          Fetching formulary documents for this indication
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
            <AlertCircle className="h-8 w-8 text-destructive" />
          </div>
          <h3 className="text-sm font-semibold mb-2">Unable to Load Documents</h3>
          <p className="text-xs font-normal text-muted-foreground">{error}</p>
        </CardContent>
      </Card>
    );
  }

  const hasContent = documentLinks.length > 0 || documents.length > 0;

  if (!hasContent) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
            <FileText className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="text-base font-semibold mb-2">Formulary Documents Not Provided</h3>
          <p className="text-sm text-muted-foreground">
            The plan has not published formulary documents for this indication
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* External Links Section */}
      {documentLinks.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-semibold">External Document Links</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {documentLinks.map((link, idx) => (
              <button
                key={idx}
                className="w-100 d-flex justify-content-between align-items-center p-3 border rounded bg-light text-start text-xs hover:bg-muted transition-colors"
                style={{ cursor: 'pointer', background: 'var(--bs-light)' }}
                onClick={() => {
                  showToast('Opening in new tab...', 'info', 2000);
                  window.open(link.url, '_blank', 'noopener,noreferrer');
                }}
                aria-label={`Open ${link.text} in new tab`}
              >
                <span>{link.text}</span>
                <ExternalLink className="h-4 w-4 ms-2 flex-shrink-0" style={{ minWidth: '16px' }} />
              </button>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Downloadable Documents Section */}
      {documents.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-semibold">Formulary Documents</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {documents.map((doc) => (
              <div
                key={doc.DocumentId}
                className="d-flex align-items-center justify-content-between p-3 border rounded hover:bg-muted/50 transition-colors"
              >
                <div className="flex-grow-1 min-w-0 space-y-2">
                  <div className="d-flex align-items-center gap-2">
                    <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <span className="font-medium text-xs">{doc.DocumentName}</span>
                  </div>
                  <Badge variant="static" className="text-xs">
                    {doc.DocumentTypeName} PDF
                  </Badge>
                </div>
                <div className="d-flex gap-2 ms-3">
                  <button
                    className="btn btn-sm p-2 d-flex align-items-center justify-content-center"
                    style={{ 
                      width: '32px', 
                      height: '32px', 
                      border: '1px solid #dee2e6',
                      backgroundColor: 'white',
                      color: '#6c757d'
                    }}
                    onClick={() => handleViewDocument(doc.DocumentId, doc.DocumentName, doc.DocumentTypeName)}
                    disabled={loadingDocId === doc.DocumentId}
                    aria-label={`View ${doc.DocumentTypeName} PDF in new tab`}
                    title={`View ${doc.DocumentTypeName} PDF in new tab`}
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  <button
                    className="btn btn-sm p-2 d-flex align-items-center justify-content-center"
                    style={{ 
                      width: '32px', 
                      height: '32px', 
                      border: '1px solid #dee2e6',
                      backgroundColor: 'white',
                      color: '#6c757d'
                    }}
                    onClick={() => handleDownloadDocument(doc.DocumentId, doc.DocumentName, doc.DocumentTypeName)}
                    disabled={loadingDocId === doc.DocumentId}
                    aria-label={`Download ${doc.DocumentTypeName} PDF`}
                    title={`Download ${doc.DocumentTypeName} PDF`}
                  >
                    <Download className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default FormularyDocumentsContent;
